import { motion } from 'motion/react'
import type { Pillar } from './data'

interface PillarRowProps {
  pillar: Pillar
  index: number
  total: number
  isActive: boolean
  isDimmed: boolean
  rowRef: (el: HTMLDivElement | null) => void
}

export function PillarRow({
  pillar,
  index,
  total,
  isActive,
  isDimmed,
  rowRef,
}: PillarRowProps) {
  return (
    <div ref={rowRef} id={`pillar-${pillar.number}`}>
      <motion.div
        animate={{ opacity: isDimmed ? 0.32 : 1 }}
        transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
        className={`relative flex flex-col gap-6 py-10 md:flex-row md:items-start md:gap-7 px-7 md:py-12 ${
          index < total - 1 ? 'border-b border-white/8' : ''
        }`}
      >
        {/* Active golden accent bar on left edge */}
        <motion.div
          className="absolute left-0 top-0 w-[3px] bg-primary-500 origin-top rounded-full"
          animate={{ scaleY: isActive ? 1 : 0, opacity: isActive ? 1 : 0 }}
          transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
          style={{ height: '100%' }}
        />

        {/* Active glow backdrop */}
        <motion.div
          className="pointer-events-none absolute inset-0"
          animate={{ opacity: isActive ? 1 : 0 }}
          transition={{ duration: 0.45 }}
          style={{
            background:
              'radial-gradient(ellipse 70% 60% at 30% 50%, rgba(255,182,6,0.055) 0%, transparent 70%)',
          }}
        />

        {/* Ghost number — large, behind content */}
        <span
          className="pointer-events-none absolute right-0 top-0  select-none text-[100px] font-bold leading-none max-md:hidden transition-colors duration-500"
          style={{
            color: isActive
              ? 'rgba(255,182,6,0.07)'
              : 'rgba(255,255,255,0.025)',
          }}
          aria-hidden="true"
        >
          {pillar.number}
        </span>

        {/* Number column */}
        <div className=" items-center gap-4 md:flex-col md:items-end md:w-16  md:pt-1">
          <span
            className="tabular-nums text-4xl font-bold leading-none transition-colors duration-500 md:text-5xl"
            style={{
              color: isActive ? 'rgba(255,182,6,0.9)' : 'rgba(255,182,6,0.2)',
            }}
          >
            {pillar.number}
          </span>
        </div>

        {/* Vertical divider (desktop) */}
        <div
          className="hidden md:block w-px flex-shrink-0 self-stretch transition-colors duration-500"
          style={{
            background: isActive
              ? 'rgba(255,182,6,0.38)'
              : 'rgba(255,255,255,0.08)',
          }}
        />

        {/* Icon (desktop) */}
        <div
          className="hidden flex-shrink-0 md:flex size-14 items-center justify-center rounded-xl border p-3 transition-all duration-500"
          style={{
            borderColor: isActive ? '#FFB606' : 'rgba(255,255,255,0.10)',
            background: isActive ? '#FFB606' : 'rgba(255,255,255,0.05)',
            color: isActive ? '#1a1200' : 'var(--primary-300)',
          }}
        >
          <pillar.icon />
        </div>

        {/* Main content */}
        <div className="flex flex-1 flex-col gap-3 md:gap-4">
          {/* Mobile: icon + label */}
          <div className="flex items-center gap-3 md:hidden">
            <div className="flex size-10 flex-shrink-0 items-center justify-center rounded-lg border border-white/10 bg-white/5 p-2.5 text-primary-300">
              <pillar.icon />
            </div>
            <p className="text-xs font-bold uppercase tracking-[0.25em] text-primary-400">
              {pillar.label}
            </p>
          </div>

          {/* Desktop label */}
          <p
            className="hidden text-xs font-bold uppercase tracking-[0.25em] md:block transition-colors duration-500"
            style={{ color: isActive ? '#FFB606' : 'rgba(196,160,0,0.65)' }}
          >
            {pillar.label}
          </p>

          <h3
            className="text-2xl font-bold leading-snug md:text-3xl transition-colors duration-500"
            style={{ color: isActive ? '#ffffff' : 'rgba(255,255,255,0.65)' }}
          >
            {pillar.title}
          </h3>

          <p
            className="text-base leading-relaxed transition-colors duration-500"
            style={{
              color: isActive ? 'rgb(212,212,212)' : 'rgba(163,163,163,0.6)',
            }}
          >
            {pillar.description}
          </p>

          {/* Tags — mobile */}
          <div className="flex flex-wrap gap-2 pt-1 md:hidden">
            {pillar.tags.map((tag) => (
              <span
                key={tag}
                className="rounded-full border border-white/10 bg-white/5 px-3 py-1 text-xs font-medium text-neutral-400"
              >
                {tag}
              </span>
            ))}
          </div>
        </div>

        {/* Tags — desktop right column */}
        <div className="hidden md:flex flex-col gap-2 w-40 flex-shrink-0 pt-8">
          {pillar.tags.map((tag) => (
            <span
              key={tag}
              className="rounded-full border px-3 py-1.5 text-xs font-medium text-center transition-all duration-500"
              style={{
                borderColor: isActive
                  ? 'rgba(255,182,6,0.38)'
                  : 'rgba(255,255,255,0.10)',
                background: isActive
                  ? 'rgba(255,182,6,0.08)'
                  : 'rgba(255,255,255,0.04)',
                color: isActive ? '#FFB606' : 'rgba(163,163,163,0.5)',
              }}
            >
              {tag}
            </span>
          ))}
        </div>
      </motion.div>
    </div>
  )
}
